import React, { useEffect, useState } from 'react';
import { X, CheckSquare, AlertCircle, MapPin, Map as MapIcon } from 'lucide-react';
import C from '../../constants/colors';
import * as actionsApi from '../../api/actions';
import * as usersApi from '../../api/users';
import MapPickerModal from './MapPickerModal';

/**
 * ActionFormModal — admin creates a new Action.
 *
 * Props:
 *   sites             — full sites array (for site picker)
 *   incidents         — full incidents array (for linked-incident picker)
 *   preselectedSiteId — pre-fill site (e.g. when opened from ActionsView site context)
 *   preselectedIncidentId — pre-fill incident link (e.g. when opened from IncidentDetail)
 *   onCreated(action) — called after successful create
 *   onClose           — close handler
 */
export default function ActionFormModal({
  sites, zones, incidents,
  preselectedSiteId, preselectedIncidentId,
  onCreated, onClose,
}) {
  const [form, setForm] = useState({
    description: '',
    siteId: preselectedSiteId || '',
    incidentId: preselectedIncidentId || '',
    dueDate: '',
    assignedUserId: '',
    gpsLat: null,
    gpsLng: null,
  });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mapPickerOpen, setMapPickerOpen] = useState(false);

  useEffect(() => {
    usersApi.listUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  // When admin picks an incident, auto-fill the site from that incident
  // (only if site isn't already locked from the parent).
  useEffect(() => {
    if (!form.incidentId || preselectedSiteId) return;
    const inc = (incidents || []).find(i => i.id === form.incidentId);
    if (inc?.siteId && !form.siteId) {
      setForm(p => ({ ...p, siteId: inc.siteId }));
    }
  }, [form.incidentId, incidents, preselectedSiteId, form.siteId]);

  const incidentsForSite = (incidents || []).filter(i =>
    !form.siteId || i.siteId === form.siteId
  );

  async function handleSubmit() {
    setError('');
    if (!form.description.trim()) { setError('Description is required.'); return; }
    if (!form.siteId && !form.incidentId) {
      setError('Pick a site (or link an incident — site will be inherited).');
      return;
    }
    setSaving(true);
    try {
      const created = await actionsApi.create({
        siteId: form.siteId || undefined,
        description: form.description.trim(),
        dueDate: form.dueDate || undefined,
        incidentId: form.incidentId || undefined,
        assignedUserId: form.assignedUserId || undefined,
        gpsLat: form.gpsLat ?? undefined,
        gpsLng: form.gpsLng ?? undefined,
      });
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to create action');
      setSaving(false);
    }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '28px', width: '520px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <CheckSquare size={18} color={C.blue} />
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary, margin: 0, flex: 1 }}>New Action</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
            <X size={18} />
          </button>
        </div>

        {error && (
          <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px', color: C.red, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* Description */}
        <Field label="Description *">
          <textarea
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="What needs to be done?"
            rows={3}
            style={inputStyle()}
          />
        </Field>

        {/* Site */}
        <Field label={`Site *${preselectedSiteId ? '  (set by context)' : ''}`}>
          <select
            value={form.siteId}
            disabled={!!preselectedSiteId}
            onChange={e => setForm(p => ({ ...p, siteId: e.target.value }))}
            style={inputStyle()}>
            <option value="">Select site…</option>
            {(sites || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>

        {/* Linked Incident */}
        <Field label={`Linked Incident${preselectedIncidentId ? '  (set by context)' : '  (optional)'}`}>
          <select
            value={form.incidentId}
            disabled={!!preselectedIncidentId}
            onChange={e => setForm(p => ({ ...p, incidentId: e.target.value }))}
            style={inputStyle()}>
            <option value="">— Standalone (no incident link) —</option>
            {incidentsForSite.map(i => (
              <option key={i.id} value={i.id}>
                {i.incidentType?.replace(/_/g, ' ')} · {(i.description || '').slice(0, 50)}
              </option>
            ))}
          </select>
        </Field>

        {/* Due Date */}
        <Field label="Due Date (optional)">
          <input
            type="date"
            value={form.dueDate}
            onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))}
            style={inputStyle()}
          />
        </Field>

        {/* Assignee */}
        <Field label="Assignee (optional)">
          <select
            value={form.assignedUserId}
            onChange={e => setForm(p => ({ ...p, assignedUserId: e.target.value }))}
            style={inputStyle()}>
            <option value="">— Unassigned (assign later) —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
        </Field>

        {/* GPS picker */}
        <Field label="GPS Location (optional)">
          {form.gpsLat != null && form.gpsLng != null ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px' }}>
              <MapPin size={14} color={C.green} />
              <span style={{ fontSize: '12px', color: C.textPrimary, fontFamily: 'JetBrains Mono, monospace', flex: 1 }}>
                {form.gpsLat.toFixed(5)}, {form.gpsLng.toFixed(5)}
              </span>
              <button type="button" onClick={() => setMapPickerOpen(true)}
                style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Change
              </button>
              <button type="button" onClick={() => setForm(p => ({ ...p, gpsLat: null, gpsLng: null }))}
                style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${C.red}44`, background: C.red + '11', color: C.red, fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Clear
              </button>
            </div>
          ) : (
            <button type="button"
              onClick={() => setMapPickerOpen(true)}
              disabled={!form.siteId}
              title={!form.siteId ? 'Pick a site first' : undefined}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: `1px dashed ${form.siteId ? C.blue + '55' : C.border}`,
                background: form.siteId ? C.blue + '11' : C.surface,
                color: form.siteId ? C.blue : C.textMuted,
                fontSize: '13px', fontWeight: 600,
                cursor: form.siteId ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <MapIcon size={14} />
              Pick on Map
              {!form.siteId && <span style={{ fontSize: '11px', fontStyle: 'italic', marginLeft: '4px' }}>(select a site first)</span>}
            </button>
          )}
        </Field>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: saving ? C.surface : C.blue, color: saving ? C.textMuted : '#fff', fontSize: '13px', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
            {saving ? 'Creating…' : 'Create Action'}
          </button>
        </div>
      </div>

      {mapPickerOpen && (
        <MapPickerModal
          site={(sites || []).find(s => s.id === form.siteId) || null}
          zones={(zones || []).filter(z => z.siteId === form.siteId)}
          initialLat={form.gpsLat}
          initialLng={form.gpsLng}
          onPick={({ lat, lng }) => setForm(p => ({ ...p, gpsLat: lat, gpsLng: lng }))}
          onClose={() => setMapPickerOpen(false)}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function inputStyle() {
  return {
    width: '100%', boxSizing: 'border-box',
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px',
    padding: '8px 12px', color: C.textPrimary, fontSize: '13px',
    fontFamily: 'inherit', outline: 'none', resize: 'vertical',
  };
}
